import { Link } from '@tanstack/react-router';
import { BookOpen, ChevronRight } from 'lucide-react';

import { EmptyState } from '@apps/web/components/ui/empty_state';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { useAdminAgentScratchpadNotesForRun } from '@apps/web/features/admin_agent_scratchpad/api/queries';
import { ScopeBadge } from '@apps/web/features/admin_agent_scratchpad/ui/scope_badge';
import { RelativeTimestamp } from '@apps/web/features/admin_agent_scratchpad/ui/timestamp';
import { VolatilityGlyph } from '@apps/web/features/admin_agent_scratchpad/ui/volatility_glyph';
import { cn } from '@apps/web/lib/utils';
import { type AdminScratchpadNoteSummaryRead } from '@libraries/typescript/api_client';

/**
 * Compact panel listing scratchpad notes created or last-updated by one
 * agent run. Rendered inside the run inspector today; self-contained SWR,
 * empty and loading states.
 */
export function RunScratchpadNotesPanel({
    runId,
    className,
}: {
    readonly runId: string;
    readonly className?: string;
}) {
    const { data, error, isLoading, hasRequestFailed } =
        useAdminAgentScratchpadNotesForRun(runId);

    return (
        <section
            data-testid="run-scratchpad-notes-panel"
            className={cn(
                'border-border/60 bg-card/40 flex flex-col rounded-lg border',
                className,
            )}
        >
            <header className="border-border/50 flex items-center justify-between border-b px-3 py-2 text-xs font-semibold tracking-widest uppercase">
                <span className="text-muted-foreground flex items-center gap-1.5">
                    <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                    Scratchpad notes touched
                </span>
                {data?.notes.length ? (
                    <span className="text-muted-foreground/70 font-normal tracking-normal normal-case tabular-nums">
                        {data.notes.length}
                    </span>
                ) : null}
            </header>
            <PanelBody
                notes={data?.notes ?? []}
                isLoading={isLoading && data === undefined}
                hasError={
                    error !== undefined ||
                    (hasRequestFailed && data === undefined)
                }
            />
        </section>
    );
}

function PanelBody({
    notes,
    isLoading,
    hasError,
}: {
    readonly notes: ReadonlyArray<AdminScratchpadNoteSummaryRead>;
    readonly isLoading: boolean;
    readonly hasError: boolean;
}) {
    if (isLoading) {
        return (
            <div className="flex flex-col gap-2 p-3">
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/5" />
            </div>
        );
    }
    if (hasError) {
        return (
            <p className="text-destructive p-3 text-xs">
                Failed to load scratchpad notes for this run.
            </p>
        );
    }
    if (notes.length === 0) {
        return (
            <EmptyState
                variant="compact"
                icon={BookOpen}
                title="No scratchpad writes this run"
                description="The agent didn’t record anything durable here."
            />
        );
    }
    return (
        <ul className="divide-border/40 flex flex-col divide-y">
            {notes.map((note) => (
                <li key={note.id}>
                    <RunNoteRow note={note} />
                </li>
            ))}
        </ul>
    );
}

/**
 * Row uses the same "overlay Link + interactive controls above" pattern as
 * the vault index row — the whole card is a link target but the timestamp
 * button lives outside the anchor so `<a><button>` never occurs.
 */
function RunNoteRow({
    note,
}: {
    readonly note: AdminScratchpadNoteSummaryRead;
}) {
    return (
        <div
            data-testid="run-scratchpad-note-row"
            className={cn(
                'group hover:bg-muted/40 relative flex items-center gap-2 px-3 py-2 transition-colors',
                note.retired_at !== null && 'opacity-70',
            )}
        >
            <Link
                to="/admin/agent/scratchpad/$note-id"
                params={{ 'note-id': note.id }}
                search={{ page: 1, revisions_page: 1 }}
                aria-label={`Open scratchpad note ${note.title}`}
                className="absolute inset-0 z-0 focus-visible:ring-1 focus-visible:outline-none focus-visible:ring-inset"
            />
            <VolatilityGlyph volatility={note.volatility} />
            <span className="pointer-events-none relative z-10 min-w-0 flex-1">
                <span className="text-foreground/95 truncate text-sm font-medium">
                    {note.title}
                </span>
                <span className="text-muted-foreground block truncate text-xs">
                    {note.one_liner}
                </span>
            </span>
            <span className="pointer-events-auto relative z-10 hidden shrink-0 md:inline-flex">
                <ScopeBadge
                    scopeType={note.scope_type}
                    scopeId={note.scope_id}
                    label={note.scope.organization_name}
                />
            </span>
            <span className="pointer-events-auto relative z-10 hidden shrink-0 md:inline">
                <RelativeTimestamp
                    iso={note.updated_at}
                    organizationTimezone={note.scope.organization_timezone}
                    className="text-[11px] tabular-nums"
                />
            </span>
            <ChevronRight className="text-muted-foreground/40 pointer-events-none relative z-10 h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
    );
}
